// /src/components/MP3TagEditor.js
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import seo from '../seo';

import {
  Upload, Music, Save, Download, Search, FileText, Image, Folder,
  Tag, FileDown, Info
} from 'lucide-react';

import {
  readAudioTags,
  fallbackTagsFromFilename,
  createModifiedMp3Blob,
  digitsOnly,
  cleanNulls
} from './MP3TagProcessors';

import {
  BUILT_IN_SOURCES,
  searchTagSource,
  fetchCoverArtAsDataUrl,
  CUSTOM_SOURCES_STORAGE_KEY,
  normalizeCustomSourceInput,
  defaultCustomFieldMap
} from './TagSources';

import {
  exportCollectionToCSV,
  exportCollectionToHTML,
  exportCollectionToJSON
} from './ExportUtils';

import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import styles from './MP3TagEditor.module.css';

const DISCOGS_TOKEN_STORAGE_KEY = 'kemp3-discogs-token';
const DISCOGS_KEY_STORAGE_KEY = 'kemp3-discogs-key';
const DISCOGS_SECRET_STORAGE_KEY = 'kemp3-discogs-secret';

const TRACK_FIELD_ORDER = [
  'title', 'artist', 'album', 'albumArtist',
  'track', 'totalTracks', 'disc', 'totalDiscs',
  'year', 'genre', 'composer', 'bpm',
  'key', 'language', 'copyright', 'encoder'
];

const BASIC_TRACK_FIELDS = ['title', 'artist', 'album', 'albumArtist', 'year', 'genre', 'track'];
const ALBUM_FIELDS_BASIC = ['album', 'albumArtist', 'year', 'genre'];
const ALBUM_FIELDS_ADVANCED = [...ALBUM_FIELDS_BASIC, 'disc', 'totalDiscs', 'totalTracks'];

export default function MP3TagEditor() {
  const [files, setFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [activeTab, setActiveTab] = useState('tags');
  const [searchQuery, setSearchQuery] = useState('');
  const [coverArt, setCoverArt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Contexte
  const { language, t } = useLanguage();
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  const fileInputRef = useRef(null);
  const supportedFormats = useMemo(() => ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'], []);

  const defaultTags = useMemo(() => ({
    title: '', artist: '', album: '', albumArtist: '', year: '',
    track: '', totalTracks: '', disc: '', totalDiscs: '',
    genre: '', comment: '', composer: '', bpm: '', key: '',
    language: '', copyright: '', encoder: '', lyrics: ''
  }), []);

  const [currentTags, setCurrentTags] = useState(defaultTags);

  const albumDefaultTags = useMemo(() => ({
    album: '',
    albumArtist: '',
    year: '',
    genre: '',
    comment: '',
    disc: '',
    totalDiscs: '',
    totalTracks: '',
    coverArt: null
  }), []);

  const [albumTags, setAlbumTags] = useState(albumDefaultTags);
  const [albumMode, setAlbumMode] = useState(false);
  const [tagsViewMode, setTagsViewMode] = useState('album');
  const [advancedMode, setAdvancedMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: '',
    cancelLabel: '',
    onConfirm: null
  });

  const [customSources, setCustomSources] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(CUSTOM_SOURCES_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(normalizeCustomSourceInput);
    } catch (error) {
      console.warn('Failed to load custom tag sources from storage', error);
      return [];
    }
  });

  const [selectedSourceId, setSelectedSourceId] = useState(() => BUILT_IN_SOURCES[0]?.id || 'musicbrainz');
  const [sourceQuery, setSourceQuery] = useState({ query: '', artist: '', album: '' });
  const [sourceResults, setSourceResults] = useState([]);
  const [sourceStatus, setSourceStatus] = useState('idle');
  const [sourceError, setSourceError] = useState(null);
  const [isApplyingSource, setIsApplyingSource] = useState(false);

  const [discogsToken, setDiscogsToken] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      return window.localStorage.getItem(DISCOGS_TOKEN_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  const [discogsKey, setDiscogsKey] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      return window.localStorage.getItem(DISCOGS_KEY_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  const [discogsSecret, setDiscogsSecret] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      return window.localStorage.getItem(DISCOGS_SECRET_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const tabDefinitions = useMemo(() => ([
    { id: 'tags', label: t.tags, icon: Tag, hideOnMobile: false },
    { id: 'cover', label: t.cover, icon: Image, hideOnMobile: false },
    { id: 'sources', label: t.sources, icon: Search, hideOnMobile: true },
    { id: 'export', label: t.export, icon: FileDown, hideOnMobile: true }
  ]), [t]);

  const visibleTabs = useMemo(() => (
    isMobile ? tabDefinitions.filter(tab => !tab.hideOnMobile) : tabDefinitions
  ), [tabDefinitions, isMobile]);

  useEffect(() => {
    if (!visibleTabs.length) return;
    if (visibleTabs.some(tab => tab.id === activeTab)) return;
    setActiveTab(visibleTabs[0].id);
  }, [visibleTabs, activeTab]);

  const toggleBaseClasses = 'px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900';
  const toggleTextClass = theme === 'dark' ? styles.toggleTextDark : styles.toggleTextLight;
  const albumToggleClass = albumMode
    ? `${toggleBaseClasses} ${toggleTextClass} bg-green-600 hover:bg-green-700 focus:ring-green-300`
    : `${toggleBaseClasses} ${toggleTextClass} bg-blue-600 hover:bg-blue-700 focus:ring-blue-300`;
  const advancedToggleClass = advancedMode
    ? `${toggleBaseClasses} ${toggleTextClass} bg-green-500 hover:bg-green-600 focus:ring-green-300`
    : `${toggleBaseClasses} ${toggleTextClass} bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-300`;

  const closeConfirm = useCallback(() => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  }, []);

  const showConfirm = useCallback((options) => {
    setConfirmDialog({
      open: true,
      title: options.title,
      message: options.message,
      confirmLabel: options.confirmLabel,
      cancelLabel: options.cancelLabel,
      onConfirm: options.onConfirm || null
    });
  }, []);

  const [customSourceDraft, setCustomSourceDraft] = useState({
    name: '',
    urlTemplate: '',
    resultPath: '',
    fieldMap: {
      title: defaultCustomFieldMap.title,
      artist: defaultCustomFieldMap.artist,
      album: defaultCustomFieldMap.album,
      albumArtist: '',
      year: '',
      genre: '',
      comment: '',
      coverUrl: defaultCustomFieldMap.coverUrl,
    },
    tagsJson: '',
  });
  const [customSourceError, setCustomSourceError] = useState(null);

  const combinedSources = useMemo(
    () => [...BUILT_IN_SOURCES, ...customSources],
    [customSources]
  );

  const activeSource = useMemo(() => {
    if (!combinedSources.length) return null;
    const found = combinedSources.find((src) => src.id === selectedSourceId);
    return found || combinedSources[0];
  }, [combinedSources, selectedSourceId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(CUSTOM_SOURCES_STORAGE_KEY, JSON.stringify(customSources));
    } catch (error) {
      console.warn('Failed to persist custom tag sources', error);
    }
  }, [customSources]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(DISCOGS_TOKEN_STORAGE_KEY, discogsToken || '');
    } catch (error) {
      console.warn('Failed to persist Discogs token', error);
    }
  }, [discogsToken]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(DISCOGS_KEY_STORAGE_KEY, discogsKey || '');
    } catch (error) {
      console.warn('Failed to persist Discogs key', error);
    }
  }, [discogsKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(DISCOGS_SECRET_STORAGE_KEY, discogsSecret || '');
    } catch (error) {
      console.warn('Failed to persist Discogs secret', error);
    }
  }, [discogsSecret]);

  useEffect(() => {
    if (!combinedSources.length) {
      setSelectedSourceId('');
      return;
    }
    if (!combinedSources.some((src) => src.id === selectedSourceId)) {
      setSelectedSourceId(combinedSources[0].id);
    }
  }, [combinedSources, selectedSourceId]);

  useEffect(() => {
    setSourceResults([]);
    setSourceStatus('idle');
    setSourceError(null);
    setIsApplyingSource(false);
  }, [selectedSourceId]);

  useEffect(() => {
    if (!currentFile) {
      setTagsViewMode(albumMode ? 'album' : 'track');
    }
  }, [albumMode, currentFile]);

  const extractTagsFromFile = useCallback(async (fileObj) => {
    setIsLoading(true);
    try {
      const extractedTags = fallbackTagsFromFilename(fileObj.file, defaultTags);
      const updatedFile = { ...fileObj, tags: extractedTags, duration: 0, bitrate: 0 };
      setFiles(prev => prev.map(f => f.id === fileObj.id ? updatedFile : f));
      if (currentFile && currentFile.id === fileObj.id) {
        setCurrentTags(extractedTags);
        setCurrentFile(updatedFile);
      }
      setAlbumTags(prev => ({
        ...prev,
        album: prev.album || extractedTags.album || '',
        albumArtist: prev.albumArtist || extractedTags.albumArtist || '',
        year: prev.year || extractedTags.year || '',
        genre: prev.genre || extractedTags.genre || '',
        comment: prev.comment || extractedTags.comment || '',
        disc: prev.disc || extractedTags.disc || '',
        totalDiscs: prev.totalDiscs || extractedTags.totalDiscs || '',
        totalTracks: prev.totalTracks || extractedTags.totalTracks || ''
      }));
    } catch (e) { console.error('Erreur fallback tags:', e); }
    setIsLoading(false);
  }, [currentFile, defaultTags]);

  const albumFields = useMemo(() => ({
    album: albumTags.album,
    albumArtist: albumTags.albumArtist,
    year: albumTags.year,
    genre: albumTags.genre,
    comment: albumTags.comment,
    disc: albumTags.disc,
    totalDiscs: albumTags.totalDiscs,
    totalTracks: albumTags.totalTracks
  }), [albumTags]);

  const computeFinalTagsForFile = useCallback((file, overriddenTags = null) => {
    const trackTags = overriddenTags || file.tags || {};
    const finalTags = { ...trackTags };

    if (!albumMode) return finalTags;

    Object.entries(albumFields).forEach(([key, value]) => {
      if (key === 'comment') return;
      finalTags[key] = value || '';
    });

    const trackComment = trackTags.comment?.toString().trim();
    finalTags.comment = trackComment && trackComment.length ? trackTags.comment : (albumTags.comment || '');

    if (!trackTags.album && albumTags.album) finalTags.album = albumTags.album;
    if (!trackTags.albumArtist && albumTags.albumArtist) finalTags.albumArtist = albumTags.albumArtist;

    const trackNumber = (trackTags.track || '').toString().trim();
    if (albumTags.totalTracks && trackNumber && !trackNumber.includes('/')) {
      finalTags.track = `${trackNumber}/${albumTags.totalTracks}`;
      finalTags.totalTracks = albumTags.totalTracks;
    } else if (!trackNumber && albumTags.totalTracks) {
      finalTags.track = `/${albumTags.totalTracks}`;
      finalTags.totalTracks = albumTags.totalTracks;
    }

    if (albumTags.disc && (!trackTags.disc || !trackTags.disc.toString().trim())) {
      finalTags.disc = albumTags.disc;
    }
    if (albumTags.totalDiscs) {
      finalTags.totalDiscs = albumTags.totalDiscs;
    }

    return finalTags;
  }, [albumMode, albumFields, albumTags]);

  const getCoverForFile = useCallback((file) => {
    if (file.coverArt) return file.coverArt;
    if (albumMode && albumTags.coverArt) return albumTags.coverArt;
    return null;
  }, [albumMode, albumTags.coverArt]);

  const getDisplayTitle = useCallback((file) => {
    const tags = computeFinalTagsForFile(file);
    return tags.title || file.name;
  }, [computeFinalTagsForFile]);

  const getDisplayArtist = useCallback((file) => {
    const tags = computeFinalTagsForFile(file);
    return tags.artist || tags.albumArtist || t.unknownArtist;
  }, [computeFinalTagsForFile, t.unknownArtist]);

  const filesWithAlbumApplied = useMemo(() => (
    albumMode
      ? files.map(file => ({
          ...file,
          tags: computeFinalTagsForFile(file),
          coverArt: getCoverForFile(file)
        }))
      : files
  ), [albumMode, files, computeFinalTagsForFile, getCoverForFile]);

  const downloadFileWithMetadata = useCallback(async (fileObj) => {
    if (!fileObj.file.name.toLowerCase().endsWith('.mp3')) {
      const url = URL.createObjectURL(fileObj.file);
      const link = document.createElement('a');
      link.href = url; link.download = fileObj.name; link.click();
      URL.revokeObjectURL(url);
      return;
    }
    setIsLoading(true);
    try {
      const finalTags = computeFinalTagsForFile(fileObj);
      const coverToApply = getCoverForFile(fileObj);
      const { blob, suggestedName } = await createModifiedMp3Blob(
        fileObj.file, finalTags, coverToApply
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = suggestedName; link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de la modification du MP3:', error);
      const url = URL.createObjectURL(fileObj.file);
      const link = document.createElement('a');
      link.href = url; link.download = fileObj.name; link.click();
      URL.revokeObjectURL(url);
      alert('Erreur lors de la modification. Fichier original téléchargé.');
    }
    setIsLoading(false);
  }, [computeFinalTagsForFile, getCoverForFile]);

  const downloadAllFilesWithMetadata = useCallback(async () => {
    setIsLoading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        // petit délai pour ne pas geler l’UI
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => setTimeout(r, 800));
        // eslint-disable-next-line no-await-in-loop
        await downloadFileWithMetadata(f);
      }
    } catch (e) { console.error('Erreur batch download:', e); }
    setIsLoading(false);
  }, [files, downloadFileWithMetadata]);

  const handleFileUpload = useCallback(async (event) => {
    const uploadedFiles = Array.from(event.target.files);
    const audioFiles = uploadedFiles.filter(file => {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      return supportedFormats.includes(ext) || file.type.startsWith('audio/');
    });
    const fileObjects = audioFiles.map((file, idx) => ({
      id: Date.now() + idx, file, name: file.name, size: file.size, type: file.type,
      lastModified: file.lastModified, tags: { ...defaultTags }, coverArt: null, duration: 0, bitrate: 0
    }));
    setFiles(prev => [...prev, ...fileObjects]);

    if (fileObjects.length > 0) {
      const firstFile = fileObjects[0];
      setCurrentFile(firstFile);
      setCurrentTags(firstFile.tags);
      for (const f of fileObjects) {
        try {
          const meta = await readAudioTags(f.file);
          const n = {
            ...defaultTags,
            title: meta.tags.title || '',
            artist: meta.tags.artist || '',
            album: meta.tags.album || '',
            albumArtist: meta.tags.band || meta.tags.albumArtist || '',
            year: meta.tags.year || '',
            track: meta.tags.track || '',
            totalTracks: meta.tags.totalTracks || '',
            disc: meta.tags.disc || '',
            totalDiscs: meta.tags.totalDiscs || '',
            genre: meta.tags.genre || '',
            comment: meta.tags.comment || 'Édité avec MP3 Tag Editor Pro',
            composer: meta.tags.composer || '',
            encoder: meta.tags.encoder || '',
            lyrics: meta.tags.lyrics || '',
            bpm: meta.tags.bpm || '',
            key: meta.tags.key || '',
            language: meta.tags.language || '',
            copyright: meta.tags.copyright || ''
          };
          setFiles(prev => prev.map(x => (x.id === f.id ? { ...x, tags: n, coverArt: meta.coverArt } : x)));
          if (f.id === firstFile.id) {
            setCurrentTags(n);
            setCoverArt(meta.coverArt || null);
            setCurrentFile(prev => prev?.id === f.id ? { ...prev, tags: n, coverArt: meta.coverArt } : prev);
            setAlbumTags(prev => ({
              ...prev,
              album: prev.album || n.album || '',
              albumArtist: prev.albumArtist || n.albumArtist || '',
              year: prev.year || n.year || '',
              genre: prev.genre || n.genre || '',
              comment: prev.comment || n.comment || '',
              disc: prev.disc || n.disc || '',
              totalDiscs: prev.totalDiscs || n.totalDiscs || '',
              totalTracks: prev.totalTracks || n.totalTracks || '',
              coverArt: prev.coverArt || meta.coverArt || null
            }));
          }
          if (!n.title && !n.artist) await extractTagsFromFile(f);
        } catch {
          await extractTagsFromFile(f);
        }
      }
    }
  }, [defaultTags, extractTagsFromFile, supportedFormats]);

  const executeConfirm = useCallback(async () => {
    const action = confirmDialog.onConfirm;
    try {
      if (action) {
        await action();
      }
    } finally {
      closeConfirm();
    }
  }, [closeConfirm, confirmDialog.onConfirm]);

  const handleTagChange = useCallback((field, value) => {
    const numericFields = ['year', 'bpm', 'totalTracks', 'totalDiscs'];
    const sanitize = (f, v) => numericFields.includes(f) ? digitsOnly(v) : cleanNulls(v);
    setCurrentTags(prev => ({ ...prev, [field]: sanitize(field, value) }));
    setHasUnsavedChanges(true);
  }, []);

  const handleAlbumTagChange = useCallback((field, value) => {
    const numericAlbumFields = ['year', 'disc', 'totalDiscs', 'totalTracks'];
    const normalizedValue = numericAlbumFields.includes(field) ? digitsOnly(value) : cleanNulls(value);
    setAlbumTags(prev => ({ ...prev, [field]: normalizedValue }));
    setHasUnsavedChanges(true);
  }, []);

  const saveTags = useCallback(async () => {
    setIsLoading(true);
    if (currentFile) {
      const updated = files.map(file => file.id === currentFile.id ? { ...file, tags: currentTags } : file);
      setFiles(updated);
      setCurrentFile({ ...currentFile, tags: currentTags });
      setHasUnsavedChanges(false);
    } else {
      setHasUnsavedChanges(false);
    }
    await new Promise(resolve => setTimeout(resolve, 500));
    setIsLoading(false);
  }, [files, currentFile, currentTags]);

  const handleFileSelect = useCallback(async (fileObj) => {
    if (hasUnsavedChanges && currentFile) {
      const shouldSave = window.confirm(t.unsavedChanges);
      if (shouldSave) { await saveTags(); }
    }
    setCurrentFile(fileObj);
    setCurrentTags(fileObj.tags || defaultTags);
    setHasUnsavedChanges(false);
    setCoverArt(fileObj.coverArt || albumTags.coverArt || null);
    setTagsViewMode('track');
  }, [hasUnsavedChanges, currentFile, t.unsavedChanges, defaultTags, saveTags, albumTags.coverArt]);

  const setCoverArtForCurrentFile = useCallback((dataUrl) => {
    setCoverArt(dataUrl || null);
    setFiles(prev => prev.map(f => f.id === currentFile?.id ? { ...f, coverArt: dataUrl || null } : f));
    setCurrentFile(prev => (prev ? { ...prev, coverArt: dataUrl || null } : prev));
    setHasUnsavedChanges(true);
  }, [currentFile]);

  const setAlbumCoverArt = useCallback((dataUrl) => {
    setAlbumTags(prev => ({ ...prev, coverArt: dataUrl || null }));
    setHasUnsavedChanges(true);
  }, []);

  const enableAlbumMode = useCallback(async () => {
    let baseFile = currentFile || (files.length ? files[0] : null);
    if (typeof window !== 'undefined' && files.length > 1) {
      const list = files
        .map((file, idx) => `${idx + 1}. ${(file.tags?.title || file.name)}`)
        .join('\n');
      const answer = window.prompt(
        `${t.albumModeSelectPrompt.replace('{count}', String(files.length))}\n${list}`,
        baseFile ? String(files.findIndex(f => f.id === baseFile.id) + 1) : '1'
      );
      if (answer) {
        const index = parseInt(answer, 10) - 1;
        if (!Number.isNaN(index) && files[index]) {
          baseFile = files[index];
        } else {
          baseFile = null;
        }
      } else {
        baseFile = null;
      }
    }

    if (baseFile) {
      setAlbumTags({
        album: baseFile.tags?.album || '',
        albumArtist: baseFile.tags?.albumArtist || '',
        year: baseFile.tags?.year || '',
        genre: baseFile.tags?.genre || '',
        comment: baseFile.tags?.comment || '',
        disc: baseFile.tags?.disc || '',
        totalDiscs: baseFile.tags?.totalDiscs || '',
        totalTracks: baseFile.tags?.totalTracks || '',
        coverArt: baseFile.coverArt || null
      });
      setAlbumCoverArt(baseFile.coverArt || null);
    } else {
      if (typeof window !== 'undefined') {
        const confirmReset = window.confirm(t.albumModeResetConfirm);
        if (!confirmReset) return;
      }
      setAlbumTags(albumDefaultTags);
      setAlbumCoverArt(null);
    }

    setAlbumMode(true);
    setTagsViewMode('album');
  }, [albumDefaultTags, currentFile, files, setAlbumCoverArt, setAlbumTags, t.albumModeResetConfirm, t.albumModeSelectPrompt]);

  const disableAlbumMode = useCallback(() => {
    setAlbumMode(false);
    setTagsViewMode('track');
  }, []);

  const toggleAlbumMode = useCallback(() => {
    showConfirm({
      title: t.confirmTitle,
      message: albumMode ? t.confirmAlbumDisable : t.confirmAlbumEnable,
      confirmLabel: t.confirmYes,
      cancelLabel: t.confirmNo,
      onConfirm: async () => {
        if (hasUnsavedChanges) {
          await saveTags();
        }
        if (albumMode) {
          disableAlbumMode();
        } else {
          await enableAlbumMode();
        }
      }
    });
  }, [albumMode, enableAlbumMode, disableAlbumMode, hasUnsavedChanges, saveTags, showConfirm, t.confirmAlbumDisable, t.confirmAlbumEnable, t.confirmNo, t.confirmTitle, t.confirmYes]);

  const applyCoverToAllFiles = useCallback(async (sourceCover) => {
    const coverToApply = sourceCover ?? coverArt ?? (albumMode ? albumTags.coverArt : null);
    if (!coverToApply) return;

    setIsLoading(true);

    // Étape 1: Supprimer toutes les images existantes
    setFiles(prev => prev.map(f => ({ ...f, coverArt: null })));
    setCoverArt(null);
    setCurrentFile(prev => (prev ? { ...prev, coverArt: null } : prev));
    if (albumMode) {
      setAlbumCoverArt(null);
    }

    // Petit délai pour s'assurer que le state est mis à jour
    await new Promise(resolve => setTimeout(resolve, 100));

    // Étape 2: Appliquer la nouvelle pochette partout
    setFiles(prev => prev.map(f => ({ ...f, coverArt: coverToApply })));
    setCoverArt(coverToApply);
    setCurrentFile(prev => (prev ? { ...prev, coverArt: coverToApply } : prev));
    if (albumMode) {
      setAlbumCoverArt(coverToApply);
    }

    setHasUnsavedChanges(true);
    setIsLoading(false);
  }, [albumMode, albumTags.coverArt, coverArt, setAlbumCoverArt]);

  const handleApplyCoverToAll = useCallback(() => {
    const coverToApply = coverArt || (albumMode ? albumTags.coverArt : null);
    if (!coverToApply) return;
    showConfirm({
      title: t.confirmTitle,
      message: albumMode ? t.confirmCoverAlbum : t.confirmCoverAll,
      confirmLabel: albumMode ? t.confirmApplyAlbum : t.confirmApplyAll,
      cancelLabel: t.confirmNo,
      onConfirm: async () => {
        applyCoverToAllFiles(coverToApply);
        await saveTags();
      }
    });
  }, [albumMode, albumTags.coverArt, applyCoverToAllFiles, coverArt, saveTags, showConfirm, t.confirmApplyAlbum, t.confirmApplyAll, t.confirmCoverAlbum, t.confirmCoverAll, t.confirmNo, t.confirmTitle]);

  const exportToCSV = useCallback(() => { exportCollectionToCSV(filesWithAlbumApplied, language); }, [filesWithAlbumApplied, language]);
  const exportToHTML = useCallback(() => { exportCollectionToHTML(filesWithAlbumApplied, language); }, [filesWithAlbumApplied, language]);
  const exportToJSON = useCallback(() => { exportCollectionToJSON(filesWithAlbumApplied); }, [filesWithAlbumApplied]);

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (file.tags.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (file.tags.artist || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (file.tags.album || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const labelForField = (field) => {
    if (t.fieldLabels && t.fieldLabels[field]) return t.fieldLabels[field];
    return (
      field === 'albumArtist' ? (language === 'fr' ? "Artiste de l'album" : 'Album Artist') :
      field === 'totalTracks' ? (language === 'fr' ? 'Nombre de pistes' : 'Total Tracks') :
      field === 'totalDiscs' ? (language === 'fr' ? 'Nombre de disques' : 'Total Discs') :
      field === 'bpm' ? 'BPM' :
      field === 'lyrics' ? (language === 'fr' ? 'Paroles' : 'Lyrics') :
      field === 'composer' ? (language === 'fr' ? 'Compositeur' : 'Composer') :
      field === 'comment' ? (language === 'fr' ? 'Commentaire' : 'Comment') :
      field === 'copyright' ? 'Copyright' :
      field === 'encoder' ? (language === 'fr' ? 'Encodeur' : 'Encoder') :
      field === 'language' ? (language === 'fr' ? 'Langue' : 'Language') :
      field === 'key' ? (language === 'fr' ? 'Tonalité' : 'Key') :
      field === 'title' ? (language === 'fr' ? 'Titre' : 'Title') :
      field === 'artist' ? (language === 'fr' ? 'Artiste' : 'Artist') :
      field === 'album' ? 'Album' :
      field === 'year' ? (language === 'fr' ? 'Année' : 'Year') :
      field === 'track' ? (language === 'fr' ? 'Piste' : 'Track') :
      field === 'genre' ? 'Genre' :
      field === 'coverUrl' ? (language === 'fr' ? 'URL Pochette' : 'Cover URL') :
      field === 'disc' ? (language === 'fr' ? 'Disque' : 'Disc') :
      field.charAt(0).toUpperCase() + field.slice(1)
    );
  };

  const sanitizeValue = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return cleanNulls(value);
    return cleanNulls(String(value));
  };

  const handleSearchTagSources = useCallback(async () => {
    if (!activeSource) return;
    const trimmedQuery = {
      query: sourceQuery.query.trim(),
      artist: sourceQuery.artist.trim(),
      album: sourceQuery.album.trim(),
    };

    if (!trimmedQuery.query && !trimmedQuery.artist && !trimmedQuery.album) {
      setSourceStatus('error');
      setSourceError({ code: 'empty-query' });
      return;
    }

    const params = { ...trimmedQuery };
    if (activeSource.id === 'discogs') {
      if (discogsToken.trim()) params.token = discogsToken.trim();
      if (discogsKey.trim()) params.key = discogsKey.trim();
      if (discogsSecret.trim()) params.secret = discogsSecret.trim();
    }

    setSourceStatus('loading');
    setSourceError(null);
    setSourceResults([]);

    try {
      const results = await searchTagSource(
        activeSource.type === 'custom' ? activeSource : activeSource.id,
        params,
        { limit: activeSource?.maxLimit || 15 }
      );
      setSourceResults(results);
      setSourceStatus('done');
    } catch (error) {
      console.error('Tag source search failed', error);
      setSourceError(error);
      setSourceStatus('error');
    }
  }, [activeSource, sourceQuery, discogsToken, discogsKey, discogsSecret]);

  const applyTagSourceResult = useCallback(async (result) => {
    if (!result) return;
    if (!currentFile) {
      setSourceStatus('error');
      setSourceError({ code: 'no-file' });
      return;
    }

    setIsApplyingSource(true);
    try {
      const updatedTags = { ...currentTags };
      const assign = (field, value, sanitizer) => {
        const sanitized = sanitizer ? sanitizer(sanitizeValue(value)) : sanitizeValue(value);
        if (sanitized) {
          updatedTags[field] = sanitized;
        }
      };

      assign('title', result.title);
      assign('artist', result.artist);
      assign('album', result.album);
      assign('albumArtist', result.albumArtist);
      assign('year', result.year, digitsOnly);
      assign('genre', result.genre);
      assign('comment', result.comment);

      if (result.tagOverrides && typeof result.tagOverrides === 'object') {
        Object.entries(result.tagOverrides).forEach(([key, value]) => {
          assign(key, value);
        });
      }

      if (albumMode) {
        setAlbumTags(prev => ({
          ...prev,
          album: result.album || prev.album,
          albumArtist: result.albumArtist || prev.albumArtist,
          year: result.year || prev.year,
          genre: result.genre || prev.genre,
          totalTracks: result.trackCount ? String(result.trackCount) : prev.totalTracks
        }));
      }

      setCurrentTags(updatedTags);
      setFiles((prev) => prev.map((file) => (
        file.id === currentFile.id ? { ...file, tags: updatedTags } : file
      )));
      setCurrentFile((prev) => (
        prev && prev.id === currentFile.id ? { ...prev, tags: updatedTags } : prev
      ));
      setHasUnsavedChanges(true);

      if (result.coverUrl) {
        try {
          const dataUrl = await fetchCoverArtAsDataUrl(result.coverUrl);
          if (dataUrl) {
            setCoverArtForCurrentFile(dataUrl);
            if (albumMode && !albumTags.coverArt) {
              setAlbumCoverArt(dataUrl);
            }
          }
        } catch (error) {
          console.warn('Cover import failed', error);
        }
      }

      setSourceError(null);
      setSourceStatus('applied');
      setActiveTab('tags');
    } catch (error) {
      console.error('Failed to apply metadata result', error);
      setSourceError(error);
      setSourceStatus('error');
    } finally {
      setIsApplyingSource(false);
    }
  }, [albumMode, albumTags.coverArt, currentFile, currentTags, setAlbumCoverArt, setAlbumTags, setCoverArtForCurrentFile]);

  const handleCustomSourceDraftChange = useCallback((field, value) => {
    setCustomSourceError(null);
    setCustomSourceDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleCustomSourceFieldMapChange = useCallback((field, value) => {
    setCustomSourceError(null);
    setCustomSourceDraft((prev) => ({
      ...prev,
      fieldMap: {
        ...prev.fieldMap,
        [field]: value,
      },
    }));
  }, []);

  const handleAddCustomSource = useCallback(() => {
    setCustomSourceError(null);
    const trimmedName = customSourceDraft.name.trim();
    const trimmedUrl = customSourceDraft.urlTemplate.trim();
    if (!trimmedName || !trimmedUrl) {
      setCustomSourceError({ code: 'missing-fields' });
      return;
    }

    let tagsMap = {};
    if (customSourceDraft.tagsJson.trim()) {
      try {
        const parsed = JSON.parse(customSourceDraft.tagsJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          tagsMap = parsed;
        } else {
          throw new Error('invalid');
        }
      } catch (error) {
        setCustomSourceError({ code: 'invalid-tags-json' });
        return;
      }
    }

    const normalized = normalizeCustomSourceInput({
      ...customSourceDraft,
      fieldMap: {
        ...customSourceDraft.fieldMap,
        tags: tagsMap,
      },
    });

    setCustomSources((prev) => [...prev, normalized]);
    setSelectedSourceId(normalized.id);
    setCustomSourceDraft({
      name: '',
      urlTemplate: '',
      resultPath: '',
      fieldMap: {
        title: defaultCustomFieldMap.title,
        artist: defaultCustomFieldMap.artist,
        album: defaultCustomFieldMap.album,
        albumArtist: '',
        year: '',
        genre: '',
        comment: '',
        coverUrl: defaultCustomFieldMap.coverUrl,
      },
      tagsJson: '',
    });
  }, [customSourceDraft]);

  const handleRemoveCustomSource = useCallback((id) => {
    setCustomSources((prev) => {
      const next = prev.filter((src) => src.id !== id);
      if (selectedSourceId === id) {
        const fallback = [...BUILT_IN_SOURCES, ...next][0];
        setSelectedSourceId(fallback ? fallback.id : '');
      }
      return next;
    });
  }, [selectedSourceId]);

  const sourceErrorMessage = useMemo(() => {
    if (!sourceError) return '';
    if (sourceError.code === 'empty-query') return t.sourceErrors.emptyQuery;
    if (sourceError.code === 'no-file') return t.sourceErrors.noFile;
    if (sourceError.code === 'missing-fields') return t.sourceErrors.missingFields;
    if (sourceError.code === 'invalid-tags-json') return t.sourceErrors.invalidTagsJson;
    if (sourceError.status === 401 || sourceError.status === 403) return t.sourceErrors.auth;
    if (sourceError.status === 429) return t.sourceErrors.rateLimit;
    return t.sourceErrors.generic;
  }, [sourceError, t.sourceErrors]);

  const customSourceErrorMessage = useMemo(() => {
    if (!customSourceError) return '';
    if (customSourceError.code === 'missing-fields') return t.sourceErrors.missingFields;
    if (customSourceError.code === 'invalid-tags-json') return t.sourceErrors.invalidTagsJson;
    return t.sourceErrors.generic;
  }, [customSourceError, t.sourceErrors]);

  return (
    <div className={theme === 'dark' ? styles.appDark : styles.appLight}>
      {confirmDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className={theme === 'dark'
            ? 'bg-gray-800 border border-gray-700 rounded-2xl max-w-md w-full p-6 text-gray-100 shadow-xl'
            : 'bg-white border border-gray-200 rounded-2xl max-w-md w-full p-6 text-gray-900 shadow-xl'}>
            <div className="flex flex-col items-center text-center gap-4">
              <img src="/logo.png" alt="Logo" className="max-w-[125px] h-auto shadow rounded-[5px] p-1" />
              <h3 className="text-lg font-semibold">
                {confirmDialog.title || t.confirmTitle}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {confirmDialog.message}
              </p>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={closeConfirm}
                  className={theme === 'dark'
                    ? 'px-4 py-2 rounded-lg border border-gray-600 text-gray-200 hover:bg-gray-700 transition-colors'
                    : 'px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors'}
                >
                  {confirmDialog.cancelLabel || t.confirmNo}
                </button>
                <button
                  type="button"
                  onClick={executeConfirm}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  {confirmDialog.confirmLabel || t.confirmYes}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* SEO (titre, canonique, description, JSON-LD) */}
      <Helmet>
        {(() => {
          const title = `${t.title} — ${t.subtitle}`;
          const description = (t.footer && t.footer.description) ? t.footer.description : t.subtitle;
          const url = seo.canonical('/mp3tagedit');
          return (
            <>
              <title>{title}</title>
              <link rel="canonical" href={url} />
              <meta name="description" content={description} />
              <meta name="robots" content="index,follow" />
              <script type="application/ld+json">
                {JSON.stringify(seo.webPage({ name: title, url, description }))}
              </script>
            </>
          );
        })()}
      </Helmet>

      {showInfo && (
        <div className={theme === 'dark' ? styles.infoBarDark : styles.infoBarLight}>
          <div className="flex">
            <div className="flex-shrink-0"><Info className="h-5 w-5 text-blue-400" /></div>
            <div className="ml-3 flex-1">
              <p className={theme === 'dark' ? styles.infoTextDark : styles.infoTextLight}>
                <strong>{language === 'fr' ? 'Éditeur MP3:' : 'MP3 Editor:'}</strong>{' '}
                {language === 'fr'
                  ? 'Modifiez les tags de vos fichiers MP3 et téléchargez-les avec les nouvelles métadonnées intégrées.'
                  : 'Edit your MP3 file tags and download them with the new integrated metadata.'}
              </p>
            </div>
            <div className="ml-auto pl-3">
              <button onClick={() => setShowInfo(false)} className={styles.infoClose}>×</button>
            </div>
          </div>
        </div>
      )}

      {/* En-tête interne (suit le thème, sans contrôles Lang/Thème) */}
      <div className={`${styles.headerBar} ${theme === 'dark' ? 'dark' : 'light'}`}>
        <div className={styles.headerInner}>
          <div className={styles.headerLeft}>
            <Music className={`${styles.headerIcon} ${theme === 'dark' ? 'dark' : 'light'}`} />
            <div>
              <h1 className={`${styles.headerTitle} ${theme === 'dark' ? 'dark' : 'light'}`}>{t.title}</h1>
              <p className={`${styles.headerSubtitle} ${theme === 'dark' ? 'dark' : 'light'}`}>{t.subtitle}</p>
            </div>
          </div>

          <div className={styles.headerRight}>
            <button
              onClick={() => fileInputRef.current?.click()}
              className={styles.primaryButton}
            >
              <Upload className="w-4 h-4" />
              {t.loadFiles}
            </button>

            {files.length > 0 && (
              <button
                onClick={downloadAllFilesWithMetadata}
                disabled={isLoading}
                className={styles.greenButton}
              >
                <Download className="w-4 h-4" />
                {t.downloadAll}
              </button>
            )}
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac"
        onChange={handleFileUpload}
        className="hidden"
      />

      <div className={styles.mainGrid}>
        <div className={theme === 'dark' ? styles.panelDark : styles.panelLight}>
          <div className="flex items-center gap-3 mb-4">
            <Folder className={styles.iconBlue} />
            <h2 className={`${styles.sectionTitle} ${theme === 'dark' ? styles.sectionTitleDark : styles.sectionTitleLight}`}>
              {t.files} ({files.length})
            </h2>
          </div>

          <div className={styles.searchWrap}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              placeholder={t.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={theme === 'dark' ? styles.searchInputDark : styles.searchInputLight}
            />
          </div>

          <div className={styles.fileList}>
            {filteredFiles.map(file => {
              const isSelected = currentFile?.id === file.id;
              return (
                <div
                  key={file.id}
                  onClick={() => handleFileSelect(file)}
                  className={
                    isSelected
                      ? (theme === 'dark' ? styles.fileItemSelectedDark : styles.fileItemSelected)
                      : (theme === 'dark' ? styles.fileItemDark : styles.fileItemLight)
                  }
                >
                  <div className="flex items-center gap-3">
                    <Music className={styles.fileItemIcon} />
                    <div className="flex-1 min-w-0">
                      <p className={theme === 'dark' ? styles.fileItemTitleDark : styles.fileItemTitleLight}>
                        {getDisplayTitle(file)}
                      </p>
                      <p className={theme === 'dark' ? styles.fileItemSubDark : styles.fileItemSubLight}>
                        {getDisplayArtist(file)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadFileWithMetadata(file); }}
                      className={styles.fileItemDownload}
                      title={t.downloadModified}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {files.length === 0 && (
            <div className={styles.centerText}>
              <Music className={styles.centerIcon} />
              <p>{t.noFiles}</p>
              <p className="text-sm">{t.clickToStart}</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
            <div className={theme === 'dark' ? styles.cardDark : styles.cardLight}>
              <div className={theme === 'dark' ? styles.tabHeaderDark : styles.tabHeaderLight}>
              {visibleTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${styles.tabBtn} ${
                    activeTab === tab.id
                      ? styles.tabBtnActive
                      : (theme === 'dark' ? styles.tabBtnInactiveDark : styles.tabBtnInactiveLight)
                  }`}
                >
                  <tab.icon className={styles.tabIcon} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === 'tags' && (
                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-black dark:text-gray-200">{t.albumModeLabel}</span>
                        <button
                          onClick={toggleAlbumMode}
                          className={albumToggleClass}
                        >
                          {albumMode ? t.albumModeDisable : t.albumModeEnable}
                        </button>
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-400">
                        {albumMode ? t.albumModeHintOn : t.albumModeHintOff}
                      </span>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-black dark:text-gray-200">{t.advancedModeLabel}</span>
                        <button
                          onClick={() => setAdvancedMode(prev => !prev)}
                          className={advancedToggleClass}
                        >
                          {advancedMode ? t.advancedModeDisable : t.advancedModeEnable}
                        </button>
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-400">
                        {advancedMode ? t.advancedModeHintOn : t.advancedModeHintOff}
                      </span>
                    </div>
                  </div>

                  {albumMode && (
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => setTagsViewMode('album')}
                        className={`${styles.tabBtn} ${tagsViewMode === 'album' ? styles.tabBtnActive : (theme === 'dark' ? styles.tabBtnInactiveDark : styles.tabBtnInactiveLight)}`}
                      >
                        {t.albumLevel}
                      </button>
                      <button
                        onClick={() => setTagsViewMode('track')}
                        disabled={!currentFile}
                        className={`${styles.tabBtn} ${tagsViewMode === 'track' ? styles.tabBtnActive : (theme === 'dark' ? styles.tabBtnInactiveDark : styles.tabBtnInactiveLight)} ${!currentFile ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {t.trackLevel}
                      </button>
                      {tagsViewMode === 'track' && !currentFile && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t.trackNeedsSelection}
                        </span>
                      )}
                    </div>
                  )}

                  {albumMode && tagsViewMode === 'album' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(advancedMode ? ALBUM_FIELDS_ADVANCED : ALBUM_FIELDS_BASIC).map((field) => (
                          <div key={field}>
                            <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                              {field === 'album' ? t.albumTitleLabel
                                : field === 'albumArtist' ? t.albumArtistLabel
                                : labelForField(field)}
                            </label>
                            <input
                              type={field === 'year' ? 'number' : 'text'}
                              value={albumTags[field] || ''}
                              onChange={(e) => handleAlbumTagChange(field, e.target.value)}
                              className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                            />
                          </div>
                        ))}
                        {advancedMode && (
                          <div className="md:col-span-2">
                            <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                              {t.albumCommentLabel}
                            </label>
                            <textarea
                              value={albumTags.comment || ''}
                              onChange={(e) => handleAlbumTagChange('comment', e.target.value)}
                              rows={3}
                              className={theme === 'dark' ? styles.textareaDark : styles.textareaLight}
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex gap-4">
                        <button onClick={saveTags} disabled={isLoading} className={styles.greenButton}>
                          <Save className="w-4 h-4" />
                          {isLoading ? t.saving : t.save}
                        </button>

                        {currentFile && (
                          <button onClick={() => downloadFileWithMetadata(currentFile)} disabled={isLoading} className={styles.primaryButton}>
                            <Download className="w-4 h-4" />
                            {t.downloadModified}
                          </button>
                        )}

                        <button
                          onClick={downloadAllFilesWithMetadata}
                          disabled={isLoading || files.length === 0}
                          className={styles.purpleButton}
                        >
                          <Download className="w-4 h-4" />
                          {t.downloadAll}
                        </button>
                      </div>
                    </div>
                  )}

                  {(!albumMode || tagsViewMode === 'track') && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(advancedMode ? TRACK_FIELD_ORDER : BASIC_TRACK_FIELDS)
                          .filter(field => !(albumMode && ALBUM_FIELDS_BASIC.includes(field)))
                          .map((field) => (
                          <div key={field}>
                            <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                              {(field === 'title') ? t.trackTitleLabel
                                : (field === 'artist') ? t.trackArtistLabel
                                : labelForField(field)}
                            </label>
                            <input
                              type={['year', 'bpm'].includes(field) ? 'number' : 'text'}
                              value={currentTags[field] || ''}
                              onChange={(e) => handleTagChange(field, e.target.value)}
                              placeholder={field === 'track' ? '1/10' : ''}
                              className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                            />
                          </div>
                        ))}
                        <div className="md:col-span-2">
                          <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                            {t.trackCommentLabel}
                          </label>
                          <textarea
                            value={currentTags.comment || ''}
                            onChange={(e) => handleTagChange('comment', e.target.value)}
                            rows={3}
                            className={theme === 'dark' ? styles.textareaDark : styles.textareaLight}
                          />
                        </div>
                        {advancedMode && (
                          <div className="md:col-span-2">
                            <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                              {labelForField('lyrics')}
                            </label>
                            <textarea
                              value={currentTags.lyrics || ''}
                              onChange={(e) => handleTagChange('lyrics', e.target.value)}
                              rows={4}
                              className={theme === 'dark' ? styles.textareaDark : styles.textareaLight}
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex gap-4">
                        <button onClick={saveTags} disabled={isLoading || (!currentFile && !albumMode)} className={styles.greenButton}>
                          <Save className="w-4 h-4" />
                          {isLoading ? t.saving : t.save}
                        </button>

                        {currentFile && (
                          <button onClick={() => downloadFileWithMetadata(currentFile)} disabled={isLoading} className={styles.primaryButton}>
                            <Download className="w-4 h-4" />
                            {t.downloadModified}
                          </button>
                        )}

                        <button
                          onClick={downloadAllFilesWithMetadata}
                          disabled={isLoading || files.length === 0}
                          className={styles.purpleButton}
                        >
                          <Download className="w-4 h-4" />
                          {t.downloadAll}
                        </button>
                      </div>
                    </div>
                  )}

                  {hasUnsavedChanges && (
                    <div className={theme === 'dark'
                      ? 'p-3 bg-yellow-900 border border-yellow-700 text-yellow-200 rounded-lg'
                      : 'p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg'}>
                      <p className="text-sm font-medium">
                        {language === 'fr' ? '⚠️ Modifications non sauvegardées' : '⚠️ Unsaved changes'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {!isMobile && activeTab === 'sources' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                        {t.sourceSelect}
                      </label>
                      <select
                        value={selectedSourceId}
                        onChange={(e) => setSelectedSourceId(e.target.value)}
                        className={theme === 'dark'
                          ? 'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'
                          : 'w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500'}
                      >
                        {combinedSources.map((src) => (
                          <option key={src.id} value={src.id} className="text-gray-900">
                            {src.name}
                          </option>
                        ))}
                      </select>
                      {activeSource?.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          {activeSource.description}
                        </p>
                      )}
                    </div>

                    {activeSource?.id === 'discogs' && (
                      <div className="space-y-3">
                        <div>
                          <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                            {t.sourceDiscogsTokenLabel}
                          </label>
                          <input
                            type="text"
                            value={discogsToken}
                            onChange={(e) => setDiscogsToken(e.target.value)}
                            placeholder={t.sourceDiscogsTokenPlaceholder}
                            className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div>
                            <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                              {t.sourceDiscogsKeyLabel}
                            </label>
                            <input
                              type="text"
                              value={discogsKey}
                              onChange={(e) => setDiscogsKey(e.target.value)}
                              placeholder={t.sourceDiscogsKeyPlaceholder}
                              className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                            />
                          </div>
                          <div>
                            <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                              {t.sourceDiscogsSecretLabel}
                            </label>
                            <input
                              type="text"
                              value={discogsSecret}
                              onChange={(e) => setDiscogsSecret(e.target.value)}
                              placeholder={t.sourceDiscogsSecretPlaceholder}
                              className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t.sourceDiscogsTokenHelp}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t.sourceDiscogsKeyHelp}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                        {t.sourceQueryLabel}
                      </label>
                      <input
                        type="text"
                        value={sourceQuery.query}
                        placeholder={t.sourceQueryPlaceholder}
                        onChange={(e) => setSourceQuery((prev) => ({ ...prev, query: e.target.value }))}
                        className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                      />
                    </div>
                    <div>
                      <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                        {t.sourceArtistLabel}
                      </label>
                      <input
                        type="text"
                        value={sourceQuery.artist}
                        placeholder={t.sourceArtistPlaceholder}
                        onChange={(e) => setSourceQuery((prev) => ({ ...prev, artist: e.target.value }))}
                        className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                      />
                    </div>
                    <div>
                      <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                        {t.sourceAlbumLabel}
                      </label>
                      <input
                        type="text"
                        value={sourceQuery.album}
                        placeholder={t.sourceAlbumPlaceholder}
                        onChange={(e) => setSourceQuery((prev) => ({ ...prev, album: e.target.value }))}
                        className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleSearchTagSources}
                      disabled={sourceStatus === 'loading'}
                      className={styles.primaryButton}
                    >
                      <Search className="w-4 h-4" />
                      {sourceStatus === 'loading' ? t.sourceSearching : t.sourceSearchButton}
                    </button>
                    <button
                      onClick={() => { setSourceResults([]); setSourceStatus('idle'); setSourceError(null); }}
                      className={theme === 'dark' ? styles.themeButtonDark : styles.themeButton}
                    >
                      {t.sourceClear}
                    </button>
                    {sourceStatus === 'done' && sourceResults.length > 0 && (
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {t.sourceResultsCount.replace('{count}', sourceResults.length)}
                      </span>
                    )}
                    {sourceStatus === 'applied' && (
                      <span className="text-sm text-green-600 dark:text-green-400">
                        {t.sourceApplied}
                      </span>
                    )}
                    {isApplyingSource && (
                      <span className="text-sm text-blue-600 dark:text-blue-300">
                        {t.sourceApplying}
                      </span>
                    )}
                  </div>

                  {sourceErrorMessage && (
                    <div className={theme === 'dark'
                      ? 'bg-red-900/70 border border-red-700 text-red-200 px-4 py-3 rounded-lg'
                      : 'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg'}>
                      {sourceErrorMessage}
                    </div>
                  )}

                  {sourceStatus === 'done' && sourceResults.length === 0 && (
                    <div className={theme === 'dark'
                      ? 'text-center text-gray-300 border border-gray-700 rounded-lg py-10'
                      : 'text-center text-gray-600 border border-gray-200 rounded-lg py-10'}>
                      <p>{t.sourceNoResults}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {sourceResults.map((result) => {
                      const sourceName = combinedSources.find((src) => src.id === result.sourceId)?.name || result.sourceId;
                      const releaseId = typeof result.id === 'string' && result.id.includes(':') ? result.id.split(':')[1] : result.raw?.id;
                      let sourceLink = '';
                      if (result.sourceId === 'musicbrainz' && releaseId) {
                        sourceLink = `https://musicbrainz.org/release/${releaseId}`;
                      } else if (result.sourceId === 'discogs') {
                        sourceLink = result.raw?.uri || result.resourceUrl || (releaseId ? `https://www.discogs.com/release/${releaseId}` : '');
                      } else if (result.raw?.url) {
                        sourceLink = result.raw.url;
                      }

                      return (
                        <div
                          key={result.id}
                          className={theme === 'dark'
                            ? 'border border-gray-700 bg-gray-800/60 rounded-xl p-4'
                            : 'border border-gray-200 bg-white rounded-xl p-4 shadow-sm'}
                        >
                          <div className="flex flex-col md:flex-row gap-4">
                            {result.coverUrl ? (
                              <img
                                src={result.coverUrl}
                                alt={result.title || result.album || 'cover'}
                                className="w-28 h-28 object-cover rounded-lg shadow"
                              />
                            ) : (
                              <div className="w-28 h-28 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-lg">
                                <Image className="w-10 h-10 text-gray-400" />
                              </div>
                            )}

                            <div className="flex-1 space-y-2">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    {result.title || result.album || t.sourceUnknownTitle}
                                  </h3>
                                  <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {sourceName}
                                  </p>
                                </div>
                                {result.score != null && (
                                  <span className="text-xs font-medium bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded-full">
                                    {t.sourceScore.replace('{score}', result.score)}
                                  </span>
                                )}
                              </div>

                              <div className="text-sm space-y-1 text-gray-700 dark:text-gray-300">
                                {result.artist && (
                                  <p>
                                    <span className="font-medium">{labelForField('artist')}:</span>{' '}
                                    {result.artist}
                                  </p>
                                )}
                                {result.album && (
                                  <p>
                                    <span className="font-medium">{labelForField('album')}:</span>{' '}
                                    {result.album}
                                  </p>
                                )}
                                {result.year && (
                                  <p>
                                    <span className="font-medium">{labelForField('year')}:</span>{' '}
                                    {result.year}
                                  </p>
                                )}
                                {result.genre && (
                                  <p>
                                    <span className="font-medium">{labelForField('genre')}:</span>{' '}
                                    {result.genre}
                                  </p>
                                )}
                                {result.country && (
                                  <p>
                                    <span className="font-medium">{t.sourceCountryLabel}:</span>{' '}
                                    {result.country}
                                  </p>
                                )}
                                {result.label && (
                                  <p>
                                    <span className="font-medium">{t.sourceLabelLabel}:</span>{' '}
                                    {result.label}
                                  </p>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-3 pt-2">
                                <button
                                  onClick={() => applyTagSourceResult(result)}
                                  disabled={isApplyingSource}
                                  className={styles.greenButton}
                                >
                                  <Tag className="w-4 h-4" />
                                  {t.sourceApply}
                                </button>
                                {sourceLink && (
                                  <a
                                    href={sourceLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm text-blue-600 dark:text-blue-300 hover:underline"
                                  >
                                    {t.sourceOpenLink}
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className={theme === 'dark' ? styles.cardDark : styles.cardLight}>
                    <h3 className="text-lg font-semibold mb-3">
                      {t.sourceCustomTitle}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      {t.sourceCustomDescription}
                    </p>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                          {t.sourceCustomName}
                        </label>
                        <input
                          type="text"
                          value={customSourceDraft.name}
                          onChange={(e) => handleCustomSourceDraftChange('name', e.target.value)}
                          placeholder={t.sourceCustomNamePlaceholder}
                          className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                        />
                      </div>
                      <div>
                        <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                          {t.sourceCustomUrl}
                        </label>
                        <input
                          type="text"
                          value={customSourceDraft.urlTemplate}
                          onChange={(e) => handleCustomSourceDraftChange('urlTemplate', e.target.value)}
                          placeholder={t.sourceCustomUrlPlaceholder}
                          className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                        />
                      </div>
                      <div>
                        <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                          {t.sourceCustomResultPath}
                        </label>
                        <input
                          type="text"
                          value={customSourceDraft.resultPath}
                          onChange={(e) => handleCustomSourceDraftChange('resultPath', e.target.value)}
                          placeholder={t.sourceCustomResultPathPlaceholder}
                          className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                        />
                      </div>
                      <div>
                        <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                          {t.sourceCustomTagsJson}
                        </label>
                        <textarea
                          value={customSourceDraft.tagsJson}
                          onChange={(e) => handleCustomSourceDraftChange('tagsJson', e.target.value)}
                          placeholder={t.sourceCustomTagsJsonPlaceholder}
                          rows={3}
                          className={theme === 'dark' ? styles.textareaDark : styles.textareaLight}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">
                      {['title', 'artist', 'album', 'albumArtist', 'year', 'genre', 'comment', 'coverUrl'].map((field) => (
                        <div key={field}>
                          <label className={theme === 'dark' ? styles.labelDark : styles.labelLight}>
                            {t.sourceCustomFieldMapLabel.replace('{field}', labelForField(field))}
                          </label>
                          <input
                            type="text"
                            value={customSourceDraft.fieldMap[field] || ''}
                            onChange={(e) => handleCustomSourceFieldMapChange(field, e.target.value)}
                            placeholder={t.sourceCustomFieldMapPlaceholder}
                            className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                          />
                        </div>
                      ))}
                    </div>

                    {customSourceErrorMessage && (
                      <div className={theme === 'dark'
                        ? 'bg-red-900/70 border border-red-700 text-red-200 px-4 py-3 rounded-lg mt-4'
                        : 'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mt-4'}>
                        {customSourceErrorMessage}
                      </div>
                    )}

                    <div className="flex items-center gap-3 mt-4">
                      <button onClick={handleAddCustomSource} className={styles.primaryButton}>
                        <Save className="w-4 h-4" />
                        {t.sourceCustomAdd}
                      </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {t.sourceCustomHint}
                      </span>
                    </div>

                    <div className="mt-6 space-y-2">
                      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {t.sourceCustomListTitle}
                      </h4>
                      {customSources.length === 0 ? (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t.sourceCustomEmpty}
                        </p>
                      ) : (
                        customSources.map((src) => (
                          <div
                            key={src.id}
                            className={theme === 'dark'
                              ? 'flex items-center justify-between bg-gray-800/70 border border-gray-700 rounded-lg px-3 py-2'
                              : 'flex items-center justify-between bg-gray-100 border border-gray-200 rounded-lg px-3 py-2'}
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{src.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                {src.urlTemplate}
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveCustomSource(src.id)}
                              className="text-sm text-red-600 dark:text-red-400 hover:underline"
                            >
                              {t.remove}
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'cover' && (
                <div className="space-y-6">
                  <div className={theme === 'dark' ? styles.cardDark : styles.cardLight}>
                    <h3 className="text-lg font-semibold mb-3">
                      {albumMode ? t.coverAlbumHeader : t.coverTrackHeader}
                    </h3>
                    <div className="text-center">
                      {(() => {
                        const displayCover = coverArt || (albumMode ? albumTags.coverArt : null);
                        return (
                          <div className={theme === 'dark' ? styles.coverDropDark : styles.coverDropLight}>
                            {displayCover ? (
                              <img
                                src={displayCover}
                                alt="Cover"
                                className="w-full h-full object-cover rounded-lg"
                                onError={() => {
                                  if (coverArt) {
                                    setCoverArtForCurrentFile(null);
                                  } else if (albumMode) {
                                    setAlbumCoverArt(null);
                                  }
                                }}
                              />
                            ) : (
                              <div className="text-center">
                                <Image className={theme === 'dark' ? styles.coverIconDark : styles.coverIconLight} />
                                <p className={theme === 'dark' ? styles.coverNoteDark : styles.coverNoteLight}>
                                  {albumMode ? (currentFile ? t.trackCoverFallback : t.trackNeedsSelection) : (currentFile ? t.clickToAddCover : t.selectFile)}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <div className="flex flex-wrap gap-4 justify-center mt-4">
                        <button
                          className={styles.primaryButton}
                          disabled={!currentFile}
                          onClick={() => {
                            if (!currentFile) return;
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (ev) => { setCoverArtForCurrentFile(ev.target.result); };
                                reader.readAsDataURL(file);
                              }
                            };
                            input.click();
                          }}
                        >
                          <Upload className="w-4 h-4" />
                          {t.loadImage}
                        </button>
                        {(coverArt || (albumMode && albumTags.coverArt)) && (
                          <button
                            className={styles.redButton}
                            onClick={() => {
                              if (coverArt) {
                                setCoverArtForCurrentFile(null);
                              } else if (albumMode) {
                                setAlbumCoverArt(null);
                              }
                            }}
                          >
                            {t.remove}
                          </button>
                        )}
                        {(coverArt || (albumMode && albumTags.coverArt)) && (
                          <button
                            className={styles.purpleButton}
                            onClick={handleApplyCoverToAll}
                          >
                            <Image className="w-4 h-4" />
                            {albumMode ? t.coverApplyAlbum : t.coverApplyAll}
                          </button>
                        )}
                      </div>
                      {currentFile && (
                        <div className={theme === 'dark' ? 'mt-4 p-3 bg-gray-700 rounded-lg text-left text-sm' : 'mt-4 p-3 bg-gray-50 rounded-lg text-left text-sm'}>
                          <p><strong>{language === 'fr' ? 'Fichier:' : 'File:'}</strong> {currentFile.name}</p>
                          <p><strong>Type:</strong> {currentFile.type}</p>
                          <p><strong>{language === 'fr' ? 'Taille:' : 'Size:'}</strong> {Math.round(currentFile.size / 1024)} KB</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-center gap-4">
                    <button
                      onClick={saveTags}
                      disabled={isLoading || (!albumMode && !currentFile)}
                      className={styles.greenButton}
                    >
                      <Save className="w-4 h-4" />
                      {isLoading ? t.saving : t.save}
                    </button>
                    {currentFile && (
                      <button onClick={() => downloadFileWithMetadata(currentFile)} disabled={isLoading} className={styles.primaryButton}>
                        <Download className="w-4 h-4" />
                        {t.downloadModified}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!isMobile && activeTab === 'export' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button onClick={exportToCSV} className="flex w-full items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-12 rounded-lg text-lg font-medium transition-all">
                      <FileText className="w-6 h-6" /> Export CSV
                    </button>
                    <button onClick={exportToHTML} className="flex w-full items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-12 rounded-lg text-lg font-medium transition-all">
                      <FileText className="w-6 h-6" /> Export HTML
                    </button>
                    <button onClick={exportToJSON} className="flex w-full items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-12 rounded-lg text-lg font-medium transition-all">
                      <FileText className="w-6 h-6" /> Export JSON
                    </button>
                  </div>

                  <div className={theme === 'dark'
                    ? 'border rounded-lg p-4 bg-blue-900 border-blue-700 text-blue-200'
                    : 'border rounded-lg p-4 bg-blue-50 border-blue-200 text-blue-800'}>
                    <h3 className={theme === 'dark' ? 'text-lg font-semibold mb-3 text-blue-100' : 'text-lg font-semibold mb-3 text-blue-900'}>
                      {t.howToUse}
                    </h3>
                    <div className="text-sm space-y-2">
                      <p><strong>1.</strong> {language === 'fr' ? 'Chargez vos fichiers MP3' : 'Load your MP3 files'}</p>
                      <p><strong>2.</strong> {language === 'fr' ? 'Modifiez les tags (titre, artiste, album, etc.)' : 'Edit tags (title, artist, album, etc.)'}</p>
                      <p><strong>3.</strong> {language === 'fr' ? 'Ajoutez une pochette si souhaité' : 'Add a cover if desired'}</p>
                      <p><strong>4.</strong> {language === 'fr' ? 'Cliquez sur "Télécharger MP3 modifié" pour obtenir le fichier avec les nouveaux tags'
                        : 'Click "Download Modified MP3" to get the file with the new tags'}</p>
                    </div>
                  </div>

                  <div className={theme === 'dark'
                    ? 'border rounded-lg p-4 bg-gray-700 border-gray-600'
                    : 'border rounded-lg p-4 bg-gray-50 border-gray-200'}>
                    <h3 className={theme === 'dark' ? 'text-lg font-semibold mb-3 text-white' : 'text-lg font-semibold mb-3 text-gray-900'}>
                      {t.collectionStats}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div className={theme === 'dark' ? styles.statsCardDark : styles.statsCardLight}>
                        <div className={styles.statsValueBlue}>{files.length}</div>
                        <div className={theme === 'dark' ? styles.statsLabelDark : styles.statsLabelLight}>
                          {language === 'fr' ? 'Fichiers' : 'Files'}
                        </div>
                      </div>
                      <div className={theme === 'dark' ? styles.statsCardDark : styles.statsCardLight}>
                        <div className={styles.statsValueGreen}>{filesWithAlbumApplied.filter(f => f.tags.artist && f.tags.artist.trim()).length}</div>
                        <div className={theme === 'dark' ? styles.statsLabelDark : styles.statsLabelLight}>{t.withArtist}</div>
                      </div>
                      <div className={theme === 'dark' ? styles.statsCardDark : styles.statsCardLight}>
                        <div className={styles.statsValuePurple}>{filesWithAlbumApplied.filter(f => f.tags.album && f.tags.album.trim()).length}</div>
                        <div className={theme === 'dark' ? styles.statsLabelDark : styles.statsLabelLight}>{t.withAlbum}</div>
                      </div>
                      <div className={theme === 'dark' ? styles.statsCardDark : styles.statsCardLight}>
                        <div className={styles.statsValueOrange}>{Math.round(files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024))}</div>
                        <div className={theme === 'dark' ? styles.statsLabelDark : styles.statsLabelLight}>MB total</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {(isLoading || currentFile) && (
            <div className={theme === 'dark' ? styles.footerStatusDark : styles.footerStatusLight}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isLoading && <div className={styles.footerSpin}></div>}
                  <span className={theme === 'dark' ? styles.footerTextDark : styles.footerTextLight}>
                    {isLoading ? t.processing : currentFile ? `${t.selectedFile} ${currentFile.name}` : t.ready}
                  </span>
                </div>
                <div className={theme === 'dark' ? styles.footerCountDark : styles.footerCountLight}>
                  {files.length} {t.filesLoaded}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
